import { Box, Center, Flex, Heading, Text, Button, VStack, HStack, Badge, Divider } from "@chakra-ui/react";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import BoxShadowContainer from "components/UIComponents/BoxShadowContainer";
import { ProfileSubscriptionsProgress } from "./ProfileSubscriptionsProgress";
import { FormattedMessage } from "react-intl";
import "./ProfileSubscriptions.css";
import { Link } from "react-router-dom";
import useApiRequest from "hooks/api/useApiHook";
import { RequestMethod, RequestName } from "hooks/api/apiHookTypes";
import ErrorText from "components/API/ErrorText";
import ProfilePurchaseHistory from "./ProfilePurchaseHistory";
import LoaderSpinner from "components/UIComponents/LoaderSpinner";
import { pricingList } from "components/Pricing/pricingList";
import { GetRequestsLeft } from "api/GetRequestsLeft";

const NoSubscriptionsFound = ({ totalRequestsLeft, requestsLoading }) => (
  <VStack spacing={6} align="stretch">
    <Center>
      <VStack spacing={4}>
        <Heading size="lg" color="text.secondary">
          <FormattedMessage id="ProfileSubscriptionsActiveText" />
        </Heading>
        <Text fontSize="lg" color="text.secondary" textAlign="center">
          <FormattedMessage id="NoActiveSubscriptionText" />
        </Text>
        <Text fontSize="md" color="text.secondary" textAlign="center" maxW="400px">
          <FormattedMessage id="ChoosePlanDescriptionText" />
        </Text>
      </VStack>
    </Center>
    
    {/* Total remaining requests for users without subscription */}
    {totalRequestsLeft !== null && totalRequestsLeft > 0 && (
      <Box p={4} bg="status.successSubtle" borderRadius="md" border="1px solid" borderColor="status.success">
        <VStack spacing={2} align="start">
          <HStack>
            <Text fontWeight="bold" fontSize="lg" color="status.successStrong">
              <FormattedMessage id="TotalRequestsLeftText" />:
            </Text>
            <Text fontSize="lg" fontWeight="bold" color="status.successStrong">
              {requestsLoading ? (
                <Text as="span" color="text.secondary">Loading...</Text>
              ) : (
                totalRequestsLeft.toLocaleString()
              )}
            </Text>
          </HStack>
          <Text fontSize="sm" color="status.successStrong">
            <FormattedMessage id="IncludingPermanentText" />
          </Text>
        </VStack>
      </Box>
    )}
    
    <Box>
      <Heading size="md" mb={4} textAlign="center" color="text.secondary">
        <FormattedMessage id="RecommendedPlansText" />
      </Heading>
      <VStack spacing={3} align="stretch">
        {pricingList.slice(1, 4).map((plan) => (
          <BoxShadowContainer key={plan.id}>
            <Box p={4}>
              <HStack justify="space-between" align="center">
              <VStack align="start" spacing={1}>
                <HStack>
                  <Text fontWeight="bold" fontSize="lg">
                    <FormattedMessage id={`${plan.name}PlanText`} />
                  </Text>
                  {plan.id === 2 && (
                    <Badge colorScheme="green" variant="subtle">
                      <FormattedMessage id="PopularText" />
                    </Badge>
                  )}
                </HStack>
                <Text fontSize="sm" color="text.secondary">
                  {plan.lookupsTotal === "infinite" ? "∞" : plan.lookupsTotal} <FormattedMessage id="RequestsPerMonthText" />
                </Text>
              </VStack>
              <VStack align="end" spacing={1}>
                <Text fontSize="xl" fontWeight="bold" color="brand.accent">
                  ${plan.price}
                </Text>
                <Text fontSize="xs" color="text.secondary">
                  <FormattedMessage id="PerMonthText" />
                </Text>
              </VStack>
            </HStack>
            </Box>
          </BoxShadowContainer>
        ))}
      </VStack>
    </Box>

    <Center>
      <Link to="/pricing">
        <Button colorScheme="blue" size="lg" variant="solid">
          <FormattedMessage id="ViewAllPlansText" />
        </Button>
      </Link>
    </Center>
  </VStack>
);

const ProfileSubscriptions = () => {
  const [subscriptions, setSubsctiptions] = useState(null);
  const [totalRequestsLeft, setTotalRequestsLeft] = useState<number | null>(null);
  const [requestsLoading, setRequestsLoading] = useState(false);

  const key = useSelector((state: RootState) => state.apiKey.apiKey);
  const { data, loading, error } = useApiRequest({
    method: RequestMethod.GET,
    name: RequestName.GET_SUBSCRIPTION,
    apiToken: key,
  });

  // Get subscription data
  useEffect(() => {
    if (
        data 
        && !loading 
        && !error 
        && data.status === "success"
      )
    {
      // console.log("Subscription data received:", data.data);
      
      // Check if there's a valid subscription
      const subscriptionData = data.data;
      const hasValidSubscription = subscriptionData && 
        subscriptionData.product_name && 
        subscriptionData.product_name !== null &&
        subscriptionData.product_name !== undefined &&
        subscriptionData.product_name !== "";
      
      if (hasValidSubscription) {
        setSubsctiptions([subscriptionData]);
      } else {
        setSubsctiptions("none");
      }
    } else setSubsctiptions("none");
  }, [data, loading, error]);

  // Get total remaining requests
  useEffect(() => {
    const fetchTotalRequests = async () => {
      if (!key || key === process.env.REACT_APP_DEFAULT_API_KEY) {
        setTotalRequestsLeft(null);
        return;
      }

      setRequestsLoading(true);
      try {
        const response = await GetRequestsLeft(key);
        if (response?.data?.requests_left !== undefined) {
          setTotalRequestsLeft(response.data.requests_left);
        }
      } catch (error) {
        // console.error("Error fetching total requests:", error);
        setTotalRequestsLeft(null);
      } finally {
        setRequestsLoading(false);
      }
    };

    fetchTotalRequests();
  }, [key]);

  const calculatePercentage = (requestsLeft, totalRequests) => {
    // Safe conversion to numbers
    const total = parseInt(String(totalRequests || 0), 10);
    const left = parseInt(String(requestsLeft || 0), 10);

    // console.log("calculatePercentage inputs:", { requestsLeft, totalRequests, total, left });

    if (!Number.isFinite(total) || total <= 0) {
      return 0;
    }

    const used = total - (Number.isFinite(left) ? left : 0);
    const ratio = used / total;
    const pct = Math.round(Math.min(100, Math.max(0, ratio * 100)));
    return pct;
  };

  // console.log(subscriptions);
  // console.log(Array.isArray(subscriptions));

  const isSubscriptionsArray = Array.isArray(subscriptions);

  return (
    <>
      {subscriptions && !loading ? (
        <>
          {" "}
          <BoxShadowContainer className="subscriptions-container">
            {isSubscriptionsArray && (
              <Heading as="h1" size="lg" mb={10}>
                <FormattedMessage id="ProfileSubscriptionsActiveText" />
              </Heading>
            )}
            
            {/* Total remaining requests - only if there's a subscription */}
            {isSubscriptionsArray && totalRequestsLeft !== null && (
              <>
                <Box mb={6} p={4} bg="status.infoSubtle" borderRadius="md" border="1px solid" borderColor="status.info">
                  <VStack spacing={2} align="start">
                    <HStack>
                      <Text fontWeight="bold" fontSize="lg" color="status.infoStrong">
                        <FormattedMessage id="TotalRequestsLeftText" />:
                      </Text>
                      <Text fontSize="lg" fontWeight="bold" color="status.infoStrong">
                        {requestsLoading ? (
                          <Text as="span" color="text.primary">Loading...</Text>
                        ) : (
                          totalRequestsLeft.toLocaleString()
                        )}
                      </Text>
                    </HStack>
                    <Text fontSize="sm" color="status.infoStrong">
                      <FormattedMessage id="IncludingPermanentText" />
                    </Text>
                  </VStack>
                </Box>
                <Divider mb={6} />
              </>
            )}
            {isSubscriptionsArray && subscriptions ? (
              subscriptions.map((subscription, index) => (
                <Flex
                  key={index}
                  justifyContent="space-between"
                  alignItems="center"
                  flexWrap="wrap"
                  className="subscriptions-progress-area"
                  mb={
                    subscriptions &&
                    subscriptions.indexOf(subscription) <
                      subscriptions.length - 1
                      ? 7
                      : 0
                  }
                >
                  <Box className="subscriptions-info" mb={6}>
                    <Flex gap="10px">
                      <Text decoration="blink">
                        <FormattedMessage id="ProfileSubscriptionsProductNameText" />{" "}:
                      </Text>
                      <Text colorScheme="blue">
                        {subscription && subscription.product_name ? (
                          <FormattedMessage
                            id={`${subscription.product_name}PlanText`}
                            defaultMessage={subscription.product_name}
                          />
                        ) : (
                          "-"
                        )}
                        {" "}
                      </Text>
                    </Flex>
                    <Flex mt={1} gap="10px">
                      <Text>
                        <FormattedMessage id="ProfileLookupsText" /> :
                      </Text>
                      <Text>{parseInt(String(subscription?.requests || 0), 10)}</Text>
                    </Flex>
                    <Flex mt={1} gap="10px">
                      <Text>
                        <FormattedMessage id="ProfileDateStartText" /> :
                      </Text>
                      <Text>{subscription?.date_start || "-"}</Text>
                    </Flex>
                    <Flex mt={1} gap="10px">
                      <Text>
                        <FormattedMessage id="ProfileDateEndText" /> :
                      </Text>
                      <Text>{subscription?.date_end || "-"}</Text>
                    </Flex>
                    <Flex mt={1} gap="10px">
                      <Text>
                        <FormattedMessage id="ProfileLookupsLeftText" /> :
                      </Text>
                      <Text>{parseInt(String(subscription?.requests_left || 0), 10)}</Text>
                    </Flex>
                  </Box>
                  <Box>
                    <ProfileSubscriptionsProgress
                      value={calculatePercentage(
                        subscription.requests_left,
                        subscription.requests
                      )}
                    />{" "}
                  </Box>
                </Flex>
              ))
            ) : (
              <NoSubscriptionsFound 
                totalRequestsLeft={totalRequestsLeft} 
                requestsLoading={requestsLoading} 
              />
            )}
          </BoxShadowContainer>
          <ProfilePurchaseHistory />
        </>
      ) : error ? (
        <ErrorText />
      ) : (
        <Center>
          <LoaderSpinner />
        </Center>
      )}
    </>
  );
};

export default ProfileSubscriptions;
